import { ScreenSize } from 'glints-aries';
import { DropShadow } from 'glints-aries/lib/@next';
import {
  borderRadius8,
  borderRadius16,
} from 'glints-aries/lib/@next/utilities/borderRadius';
import { Blue, Neutral, Yellow } from 'glints-aries/lib/@next/utilities/colors';
import styled, { css } from 'styled-components';

import GlintsPicture from 'src/components/GlintsPicture';

import { MOBILE_BREAKPOINT, SGGradients } from '../constants';
import { sgCtaBase } from './shared.sc';

export { SGInner as Inner } from './shared.sc';

export const Section = styled.section`
  padding: 80px 0;
  background: ${Neutral.B100};

  @media (max-width: ${ScreenSize.tablet}px) {
    padding: 56px 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 44px 0;
  }
`;

export const Head = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: ${ScreenSize.tablet}px) {
    margin-bottom: 28px;
  }
`;

export const Title = styled.h2`
  font-size: 38px;
  margin: 8px 0;
  color: ${Neutral.B18};
  letter-spacing: -0.015em;
  text-wrap: balance;

  @media (max-width: ${ScreenSize.tablet}px) {
    font-size: 28px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 22px;
  }
`;

export const TitleHighlight = styled.span`
  background: linear-gradient(180deg, transparent 60%, ${Yellow.Brand} 60%);
  padding: 0 2px;
`;

export const HeadDescription = styled.p`
  color: ${Neutral.B40};
  margin: 0 auto;
  max-width: 620px;
  line-height: 1.6;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 14px;
  }
`;

export const Card = styled.div`
  background: ${Neutral.B100};
  border: thin solid ${Neutral.B85};
  border-radius: ${borderRadius16};
  box-shadow: ${DropShadow.card};
  padding: 40px;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 40px;
  align-items: center;

  @media (max-width: ${ScreenSize.tablet}px) {
    grid-template-columns: 1fr;
    padding: 28px;
    gap: 28px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 22px 18px;
    gap: 20px;
  }
`;

export const Tabs = styled.div`
  display: inline-flex;
  background: ${Neutral.B99};
  padding: 4px;
  border-radius: ${borderRadius8};
  gap: 4px;
  margin-bottom: 22px;
  max-width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    display: flex;
  }
`;

export const TabIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

interface TabButtonProps {
  $active: boolean;
}

export const TabButton = styled.button<TabButtonProps>`
  border: 0;
  background: transparent;
  padding: 10px 18px;
  border-radius: ${borderRadius8};
  font-weight: 600;
  font-size: 14px;
  color: ${Neutral.B40};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;

  ${({ $active }) =>
    $active &&
    css`
      background: ${Neutral.B100};
      color: ${Neutral.B18};
      box-shadow: ${DropShadow.card};
    `}

  &:hover {
    color: ${Neutral.B18};
  }

  &:focus-visible {
    outline: 2px solid ${Blue.Brand};
    outline-offset: 2px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1;
    padding: 10px 12px;
    font-size: 13.5px;
  }
`;

export const SolTitle = styled.h3`
  font-size: 26px;
  margin: 0 0 14px;
  color: ${Neutral.B18};
  letter-spacing: -0.01em;
  line-height: 1.25;

  @media (max-width: ${ScreenSize.tablet}px) {
    font-size: 22px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 19px;
    margin-bottom: 12px;
  }
`;

export const Lede = styled.p`
  color: ${Neutral.B40};
  line-height: 1.6;
  margin: 0 0 20px;
  font-size: 15.5px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 14px;
    margin-bottom: 16px;
  }
`;

export const CtaRow = styled.div`
  margin: 0 0 28px;
`;

export const Cta = styled.a`
  ${sgCtaBase}
  background: ${Blue.Brand};
  color: ${Neutral.B100};
`;

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 18px;
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 7px 0;
  color: ${Neutral.B18};
  font-size: 14.5px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 13.5px;
  }

  &::before {
    content: '✓';
    color: ${Blue.Brand};
    font-weight: 700;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${Neutral.B99};
    display: grid;
    place-items: center;
    font-size: 12px;
  }
`;

export const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 22px;
  border-top: 1px solid ${Neutral.B85};
`;

export const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Stars = styled.span`
  color: ${Yellow.Brand};
  letter-spacing: 2px;
  font-size: 16px;
  -webkit-text-stroke: 0.5px ${Yellow.S87};
`;

export const MetaIcon = styled.span`
  display: inline-flex;
  color: ${Blue.S99};
  line-height: 1;

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

export const RatingText = styled.span`
  font-size: 13.5px;
  color: ${Neutral.B40};

  strong {
    color: ${Neutral.B18};
  }
`;

interface VisualProps {
  $bare?: boolean;
}

export const Visual = styled.div<VisualProps>`
  background: ${({ $bare }) => ($bare ? 'transparent' : SGGradients.blue)};
  border-radius: ${({ $bare }) => ($bare ? '0' : borderRadius16)};
  aspect-ratio: 4 / 3;
  position: relative;
  display: grid;
  place-items: center;
  color: ${Neutral.B100};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: ${({ $bare }) =>
      $bare
        ? 'none'
        : 'radial-gradient(rgba(255, 255, 255, 0.16) 1px, transparent 1px)'};
    background-size: 18px 18px;
    pointer-events: none;
  }
`;

export const VisualBanner = styled(GlintsPicture)`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
`;

export const VisualMock = styled.div`
  position: relative;
  z-index: 1;
  width: min(82%, 320px);
  background: ${Neutral.B100};
  border-radius: ${borderRadius16};
  padding: 18px;
  box-shadow: ${DropShadow.card};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const JobMock = styled.div`
  position: relative;
  z-index: 1;
  width: min(86%, 360px);
  background: ${Neutral.B100};
  border-radius: ${borderRadius16};
  padding: 18px;
  box-shadow: ${DropShadow.card};
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: min(90%, 320px);
    padding: 14px;
    gap: 8px;
  }
`;

export const JobMockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

export const JobMockBrand = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${SGGradients.blue};
  color: ${Neutral.B100};
  font-weight: 700;
  font-size: 16px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

export const JobMockDivider = styled.div`
  height: 1px;
  background: ${Neutral.B85};
  margin: 6px -2px 4px;
`;

export const JobMockMatchHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${Blue.S99};

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;

export const JobMockCandidate = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;

  & + & {
    border-top: 1px solid ${Neutral.B85};
  }
`;

interface JobMockAvatarProps {
  tone: 'blue' | 'yellow' | 'dark';
}

const avatarTones = {
  blue: SGGradients.blue,
  yellow: `linear-gradient(135deg, #FFE38A, #E6BB1C)`,
  dark: `linear-gradient(135deg, #2C3E50, #0A1A2A)`,
};

export const JobMockAvatar = styled.div<JobMockAvatarProps>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ tone }) => avatarTones[tone]};
  flex-shrink: 0;
`;

export const JobMockCandidateBars = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

interface JobMockMatchChipProps {
  strength: 'high' | 'mid';
}

export const JobMockMatchChip = styled.span<JobMockMatchChipProps>`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 4px 8px;
  border-radius: 999px;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  ${({ strength }) =>
    strength === 'high'
      ? css`
          color: #057a3e;
          background: #d6f4e1;
        `
      : css`
          color: ${Blue.S99};
          background: ${Neutral.B99};
        `}
`;

export const MockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

export const MockAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${Blue.Brand}, ${Blue.S99});
  flex-shrink: 0;
`;

export const MockTitleStack = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

interface MockBarProps {
  width: string;
  muted?: boolean;
}

export const MockBar = styled.div<MockBarProps>`
  height: 8px;
  border-radius: 4px;
  width: ${({ width }) => width};
  background: ${({ muted }) => (muted ? Neutral.B85 : Neutral.B18)};
  opacity: ${({ muted }) => (muted ? 0.6 : 1)};
`;

export const MockTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0 10px;
`;

export const MockTag = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: ${Blue.S99};
  background: ${Neutral.B99};
  padding: 4px 10px;
  border-radius: 999px;
`;

interface FloatBadgePosition {
  position: 'topLeft' | 'bottomRight';
}

export const FloatBadge = styled.div<FloatBadgePosition>`
  position: absolute;
  z-index: 2;
  background: ${Neutral.B100};
  color: ${Neutral.B18};
  border: thin solid ${Neutral.B85};
  border-radius: ${borderRadius8};
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: ${DropShadow.card};
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ position }) =>
    position === 'topLeft'
      ? 'top: -16px; left: -16px;'
      : 'bottom: -16px; right: -16px;'}

  @media (max-width: ${ScreenSize.tablet}px) {
    font-size: 12px;
    padding: 8px 12px;
    border-radius: 10px;
    ${({ position }) =>
      position === 'topLeft'
        ? 'top: -12px; left: -12px;'
        : 'bottom: -12px; right: -12px;'}
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 11.5px;
    padding: 6px 10px;
    gap: 6px;
    ${({ position }) =>
      position === 'topLeft'
        ? 'top: -10px; left: -8px;'
        : 'bottom: -10px; right: -8px;'}
  }
`;

export const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
`;

export const VerifiedIcon = styled.span`
  display: inline-flex;
  color: ${Blue.Brand};

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;
